import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { type ReactNode, useState } from 'react';

import { BlockKeyValuesTable } from './Table';
import { type KeyValueData, type KeyValueFieldNames } from './types';
import { type ConfigFieldProps } from '../types';
import { useSelectedNodeField } from '../useSelectedNodeField';

interface BlockKeyValuesFieldProps extends ConfigFieldProps {
  label?: ReactNode;
  helperText?: ReactNode;
  addRowHelperText?: string;
  keyValueFieldNames?: KeyValueFieldNames;
  getDefaultNewRow?: () => KeyValueData;
}

export function BlockKeyValuesField(props: BlockKeyValuesFieldProps) {
  const {
    fieldName,
    label = 'Placeholder',
    addRowHelperText = 'row',
    keyValueFieldNames = {
      key: {
        name: 'key',
        label: 'Key',
      },
      value: {
        name: 'value',
        label: 'Value',
      },
    },
    getDefaultNewRow = () => ({
      id: crypto.randomUUID(),
      [keyValueFieldNames.key.name]: '',
      [keyValueFieldNames.value.name]: '',
    }),
    ...tableProps
  } = props;

  const { setValue, value: values } = useSelectedNodeField<KeyValueData[]>(fieldName);
  const currentValues =
    values?.map((value) => {
      if (!value.id) {
        return {
          ...value,
          id: crypto.randomUUID(),
        };
      }
      return value;
    }) ?? [];

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <BlockKeyValuesTable
        fieldName={fieldName}
        label={label}
        addRowHelperText={addRowHelperText}
        keyValueFieldNames={keyValueFieldNames}
        {...tableProps}
        onAddRow={() => setValue([...currentValues, getDefaultNewRow()])}
        onToggleFullScreen={() => setIsDialogOpen(true)}
        isFullScreen={isDialogOpen}
      />

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} fullScreen>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <IconButton aria-label="Close" onClick={() => setIsDialogOpen(false)}>
            <ArrowBackIcon />
          </IconButton>
          <span>{label}</span>
        </DialogTitle>
        <DialogContent>
          <BlockKeyValuesTable
            fieldName={fieldName}
            label={label}
            addRowHelperText={addRowHelperText}
            keyValueFieldNames={keyValueFieldNames}
            {...tableProps}
            onAddRow={() => setValue([...currentValues, getDefaultNewRow()])}
            onToggleFullScreen={() => setIsDialogOpen(false)}
            isFullScreen={isDialogOpen}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIsDialogOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
